'use client'

import { useLocale } from 'next-intl'

import { useMultiScrollState } from '@/shared/lib'

import {
  AdditionalDescription,
  type Props as AdditionalDescriptionProps,
} from './additional-description'
import {
  Facilities,
  type Props as FacilitiesProps,
} from './facilities/index.pc'
import { Introduction, type Props as IntroductionProps } from './introduction'
import { Location, type Props as LocationProps } from './location'
import {
  ManagementFee,
  type Props as ManagementFeeProps,
} from './management-fee'
import { Property, type Props as PropertyProps } from './property'
import { RoomOption, type Props as RoomOptionProps } from './room-option'
import {
  DuplexStructureMessage,
  DuplexStructureMessageProps,
  SharedPlaceMessage,
  Structure,
  type StructureProps,
} from './structure'
import {
  Transportation,
  type Props as TransportationProps,
} from './transportation'
import { UsageGuide, type Props as UsageGuideProps } from './usage-guide'
import { TabValue } from '../../lib/tab'

type Props = StructureProps &
  PropertyProps &
  FacilitiesProps &
  DuplexStructureMessageProps &
  IntroductionProps &
  LocationProps &
  TransportationProps &
  RoomOptionProps &
  UsageGuideProps &
  AdditionalDescriptionProps &
  ManagementFeeProps

export const RoomInformation = ({
  roomCnt,
  bathroomCnt,
  cookroomCnt,
  sittingroomCnt,
  sharedRoom,
  sharedBathroom,
  sharedCookroom,
  propertyType,
  pyeongSize,
  squareMeterSize,
  hasElevator,
  parkingType,
  parkingNotice,
  duplexStructure,
  description,
  addrStreet,
  lat,
  lng,
  subwayStations,
  transportation,
  basicOptions,
  additionalOptions,
  missingOptions,
  usageGuide,
  additionalDescription,
  includeElectricity,
  includeWater,
  includeGas,
  managementFeeDescription,
  includeInternet,
}: Props) => {
  const locale = useLocale()
  const { registerScrollElement } = useMultiScrollState<HTMLElement>()

  const isEnglish = locale === 'en'

  return (
    <div
      className="space-y-20"
      ref={element => {
        if (element) {
          registerScrollElement(TabValue.ROOM_INFORMATION, element)
        }
      }}
    >
      {isEnglish && (
        <div className="bg-gray-0 typo-label1 mb-10 w-full rounded-sm p-3 text-center font-normal text-gray-900">
          Content written by the Landlord is displayed through automatic
          translation.
        </div>
      )}

      <section className="mb-15 space-y-10">
        <div className="flex gap-30">
          <Structure
            roomCnt={roomCnt}
            bathroomCnt={bathroomCnt}
            cookroomCnt={cookroomCnt}
            sittingroomCnt={sittingroomCnt}
            sharedRoom={sharedRoom}
            sharedBathroom={sharedBathroom}
            sharedCookroom={sharedCookroom}
          />
          <Property
            propertyType={propertyType}
            pyeongSize={pyeongSize}
            squareMeterSize={squareMeterSize}
          />
        </div>
        <SharedPlaceMessage
          sharedRoom={sharedRoom}
          sharedBathroom={sharedBathroom}
          sharedCookroom={sharedCookroom}
        />
        <DuplexStructureMessage duplexStructure={duplexStructure} />
      </section>
      <Facilities
        hasElevator={hasElevator}
        parkingType={parkingType}
        parkingNotice={parkingNotice}
      />
      <Introduction description={description} />
      <Location
        addrStreet={addrStreet}
        lat={lat}
        lng={lng}
        subwayStations={subwayStations}
      />
      <Transportation transportation={transportation} />
      <RoomOption
        basicOptions={basicOptions}
        additionalOptions={additionalOptions}
        missingOptions={missingOptions}
      />
      <UsageGuide usageGuide={usageGuide} />
      <AdditionalDescription additionalDescription={additionalDescription} />
      <ManagementFee
        includeElectricity={includeElectricity}
        includeWater={includeWater}
        includeGas={includeGas}
        includeInternet={includeInternet}
        managementFeeDescription={managementFeeDescription}
      />
    </div>
  )
}
